import { useDispatch, useSelector } from 'react-redux';
import { getValue } from '@Selectors';
import { getConfigurations, getSensorsRfiFile } from '@Actions';
import { buildSearchQueryParam } from '@Utils/buildSearchQueryParam';
import {
	ConfigurationParams,
	ConfigurationParamsRfi,
} from '@Types/configurations.types';
import { FLAGS, PaginationInfo } from '@Types';
import { Features, useIsFeatureEnabled } from '@innovyze/stylovyze';
import {
	PhysicalDataSourceType,
	VirtualDataSourceType,
} from '@Utils/constants';

type ConfigurationsPaginationHookReturn = [
	PaginationInfo,
	(
		page?: number,
		search?: string,
		sensorTypes?: string[],
		dataSourceTypes?: string[],
		virtualOrPhysical?: 'all' | 'physical' | 'virtual',
		allInQParam?: boolean | undefined,
		limit?: number,
	) => void,
	(limit?: number) => void,
];

export const useConfigurationsPagination =
	(): ConfigurationsPaginationHookReturn => {
		const dispatch = useDispatch();
		const {
			configurations: { pagination, physicalOrVirtual, paginationModel },
		} = useSelector(getValue);

		const isVirtualSensorsEnabled = useIsFeatureEnabled(
			FLAGS.ENABLE_VIRTUAL_SENSORS_ON_SENSOR_CONFIGURATION as keyof Features,
		);

		const isMuixConfigurationsTableEnabled = useIsFeatureEnabled(
			FLAGS.ENABLE_MUIX_TABLE_FOR_SENSOR_CONFIGURATION,
		);

		const fetchConfigurationsWithCriteria = (
			page = pagination.current,
			search = '',
			sensorTypes: string[] = [],
			dataSourceTypes: string[] = [],
			virtualOrPhysical = physicalOrVirtual,
			allInQParam: boolean | undefined = undefined,
			limit = paginationModel.pageSize || 10,
		): void => {
			const requestPayload: ConfigurationParams = {
				configType: 'sensor',
				limit,
				offset: page,
				virtualOrPhysical,
				allInQParam,
			};

			const queryParam = buildSearchQueryParam({
				sensorId: search,
				sensorAlias: search,
				sensorTypes: sensorTypes.includes('All')
					? undefined
					: sensorTypes?.join(','),
				dataSourceTypes: dataSourceTypes.includes('All')
					? undefined
					: dataSourceTypes?.join(','),
			});

			if (queryParam) requestPayload.q = queryParam;
			if (!isMuixConfigurationsTableEnabled && isVirtualSensorsEnabled)
				requestPayload.virtualOrPhysical = 'all';

			dispatch(getConfigurations(requestPayload));
		};

		const fetchConfigurationsRfi = (itemsCount = 1000): void => {
			const requestPayload: ConfigurationParamsRfi = {
				fileName: 'sensors_rfi.csv',
				requestParams: {
					limit: itemsCount,
					offset: 1,
					virtualOrPhysical: 'all',
					q:
						'dataSourceTypes:' +
						[
							PhysicalDataSourceType.ODBC,
							PhysicalDataSourceType.OLEDB,
							PhysicalDataSourceType.OPCUA,
							VirtualDataSourceType.BULK_UPLOAD,
						],
				},
			};

			dispatch(getSensorsRfiFile(requestPayload));
		};

		return [
			pagination,
			fetchConfigurationsWithCriteria,
			fetchConfigurationsRfi,
		];
	};
